import dayjs from 'dayjs';

import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';
import chunkArray from '@/shared/utils/chunkArray';

import generateFormFieldByMetaType from './generateFormFieldByMetaType';
import metadataFieldsConfig from './metadataFieldsConfig';

const setDefaultValueByType = (
  definition: MetadataDefinitions,
  initialData: Record<string, unknown> | null,
) => {
  if (initialData) {
    switch (definition.metatype_id as keyof typeof metadataFieldsConfig) {
      case 'date': {
        if (definition.metadata_key in initialData) {
          return dayjs(initialData[definition.metadata_key] as string);
        }
        break;
      }
      case 'dimension': {
        if (definition.metadata_key in initialData) {
          const { dimension_unit, dimension_value } = initialData[
            definition.metadata_key
          ] as {
            dimension_unit: string;
            dimension_value: string;
          };

          return {
            dimension_unit: dimension_unit || '',
            dimension_value: dimension_value || '',
          };
        }
        break;
      }
      case 'money': {
        if (definition.metadata_key in initialData) {
          const { currency_code, amount } = initialData[
            definition.metadata_key
          ] as {
            currency_code: string;
            amount: string;
          };

          return {
            currency_code: currency_code || '',
            amount: amount || '',
          };
        }
        break;
      }
      case 'rating': {
        if (definition.metadata_key in initialData) {
          if (typeof initialData[definition.metadata_key] === 'string') {
            return JSON.parse(
              initialData[definition.metadata_key] as string,
            ) as {
              scale_max: number;
              scale_min: number;
              value: number;
            };
          }

          const { scale_max, scale_min, value } = initialData[
            definition.metadata_key
          ] as {
            scale_max: number;
            scale_min: number;
            value: number;
          };

          return {
            scale_max: scale_max || '',
            scale_min: scale_min || '',
            value: value || '',
          };
        }
        break;
      }
      case 'volume':
      case 'weight': {
        if (definition.metadata_key in initialData) {
          const { unit, value } = initialData[definition.metadata_key] as {
            unit: string;
            value: number;
          };

          return {
            unit: unit || '',
            value: value || '',
          };
        }
        break;
      }
      case 'boolean': {
        if (definition.metadata_key in initialData) {
          return (initialData[definition.metadata_key] as boolean) || false;
        }
        break;
      }
      default:
        return initialData[definition.metadata_key] || '';
    }
  }

  switch (definition.metatype_id as keyof typeof metadataFieldsConfig) {
    case 'boolean':
      return false;
    case 'date':
      return dayjs();
    default:
      return '';
  }
};

const setDefaultVisibility = (
  definition: MetadataDefinitions,
  initialData: Record<string, unknown> | null,
) => {
  if (initialData) {
    return definition.metadata_key in initialData;
  }

  return definition.required_level === 'REQUIRED';
};

type Args = {
  metadataDefinitions: MetadataDefinitions[];
  initialData: Record<string, unknown> | null;
};

const getMetadataForm = ({ metadataDefinitions, initialData }: Args) => {
  let form: unknown[] = [];

  for (const definition of metadataDefinitions) {
    form = [
      ...form,
      ...generateFormFieldByMetaType({
        metadataType: definition.metatype_id,
        metadataKey: definition.metadata_key,
        fieldLabel: definition.metadata_key,
        fieldPlaceholder: `${definition.metadata_description}...`,
        value: setDefaultValueByType(definition, initialData),
        required: definition.required_level === 'REQUIRED',
        isVisible: setDefaultVisibility(definition, initialData),
      }),
    ];
  }

  const formsList = chunkArray(form.flat(), 20);
  return formsList;
};

export default getMetadataForm;
